import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { apiFetch } from "../../../core/utils/api";
import { CloseIcon } from "./icons/AIIcons";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";
import { Z_INDEX } from "../../../core/utils/zIndexScale";

// ── Option constants (mirrors CreateNewWebsite step-three) ──────────────
const PRIMARY_GOAL_OPTIONS = [
  { value: "job_search", label: "Job search" },
  { value: "graduate_school", label: "Graduate school" },
  { value: "research_academic", label: "Research/academic" },
  { value: "freelance_clients", label: "Freelance/clients" },
  { value: "personal_brand", label: "Personal brand" },
  { value: "other", label: "Other" },
];

const TONE_OPTIONS = [
  { value: "professional", label: "Professional" },
  { value: "friendly", label: "Friendly" },
  { value: "casual", label: "Casual" },
  { value: "bold", label: "Bold" },
];

const KNOWN_GOAL_VALUES = PRIMARY_GOAL_OPTIONS.map((o) => o.value);

// ── Component ───────────────────────────────────────────────────────────
/**
 * Dropdown that appears above the target-icon button and lets the user
 * view / update the WebsiteProfile tied to the current website's container.
 *
 * Props:
 *  - websiteId  : number – current website id (from EditContext)
 *  - open       : boolean
 *  - onClose    : () => void
 *  - anchorRef  : React ref to the button that triggered the dropdown (for positioning)
 */
const WebsiteProfileDropdown = ({ websiteId, open, onClose, anchorRef }) => {
  const dropdownRef = useRef(null);

  // ── local form state ──────────────────────────────────────────────
  const [primaryGoal, setPrimaryGoal] = useState(null);
  const [primaryGoalOtherText, setPrimaryGoalOtherText] = useState("");
  const [tone, setTone] = useState(null);
  const [contentFormat, setContentFormat] = useState(3);
  const [contentLength, setContentLength] = useState(3);
  const [additionalInstructions, setAdditionalInstructions] = useState("");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fixed position computed from the anchor button.
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Track the "server snapshot" so Cancel can revert without re-fetching.
  const serverSnapshot = useRef(null);

  // ── Compute position relative to anchor button ─────────────────
  useEffect(() => {
    if (!open || !anchorRef?.current) return;
    const update = () => {
      const rect = anchorRef.current.getBoundingClientRect();
      // Place the dropdown above the button, right-aligned.
      setPos({
        top: rect.top, // bottom of dropdown aligns with top of button
        left: rect.right, // right-aligned with button
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef]);

  // ── Fetch profile when dropdown opens ─────────────────────────────
  const fetchProfile = useCallback(async () => {
    if (!websiteId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/websites/${websiteId}/profile/`);
      if (!res.ok) throw new Error("Failed to load profile");
      const data = await res.json();
      applyData(data);
      serverSnapshot.current = data;
    } catch (e) {
      setError(e.message || "Could not load profile");
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => {
    if (open) fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyData = (data) => {
    const goal = data?.primary_goal ?? null;
    if (goal && !KNOWN_GOAL_VALUES.includes(goal)) {
      // Free-text goal → map to "other" + fill the text field
      setPrimaryGoal("other");
      setPrimaryGoalOtherText(goal);
    } else {
      setPrimaryGoal(goal);
      setPrimaryGoalOtherText("");
    }
    setTone(data?.tone ?? null);
    setContentFormat(data?.content_format ?? 3);
    setContentLength(data?.content_length ?? 3);
    setAdditionalInstructions(data?.additional_instructions ?? "");
  };

  // ── Handlers ──────────────────────────────────────────────────────
  const handleCancel = () => {
    // Revert to last fetched snapshot
    if (serverSnapshot.current) applyData(serverSnapshot.current);
    onClose();
  };

  const handleSave = async () => {
    if (!websiteId) return;
    setSaving(true);
    setError(null);
    try {
      const goalValue =
        primaryGoal === "other"
          ? primaryGoalOtherText.trim() || null
          : primaryGoal || null;

      const payload = {
        primary_goal: goalValue,
        tone: tone || null,
        content_format: contentFormat,
        content_length: contentLength,
        additional_instructions: additionalInstructions.trim() || null,
      };

      const res = await apiFetch(`/api/websites/${websiteId}/profile/update/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to save profile");
      const data = await res.json();
      serverSnapshot.current = data;
      onClose();
    } catch (e) {
      setError(e.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const handleGoalSelect = (value) => {
    setPrimaryGoal((prev) => (prev === value ? null : value));
    if (value !== "other") setPrimaryGoalOtherText("");
  };

  const handleToneSelect = (value) => {
    setTone((prev) => (prev === value ? null : value));
  };

  // Reset form to defaults and clear error so the user can fill from scratch.
  const handleCreateFromScratch = () => {
    setError(null);
    setPrimaryGoal(null);
    setPrimaryGoalOtherText("");
    setTone(null);
    setContentFormat(3);
    setContentLength(3);
    setAdditionalInstructions("");
    serverSnapshot.current = null;
  };

  // ── Render ────────────────────────────────────────────────────────
  if (!open) return null;

  // Whether we failed to load and should show the error-only view.
  const showErrorView = !loading && error;
  const TOP_SAFE_GAP_PX = 20;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
  // Slightly smaller than before (was 70vh) so the panel feels less tall.
  const preferredMaxHeightPx = viewportHeight ? Math.round(viewportHeight * 0.64) : 640;
  // Keep a small visible gap from the top edge when opening upward.
  const availableAboveAnchorPx = Math.max(180, Math.round(pos.top - TOP_SAFE_GAP_PX));
  const dropdownMaxHeightPx = Math.min(preferredMaxHeightPx, availableAboveAnchorPx);

  const dropdown = (
    <div
      ref={dropdownRef}
      className={`fixed ${showErrorView ? "w-96" : "w-[40rem]"} overflow-y-auto rounded-lg shadow-xl border ${borders.default} ${surfaces.elevated}`}
      style={{
        minWidth: showErrorView ? 320 : 480,
        maxHeight: dropdownMaxHeightPx,
        zIndex: Z_INDEX.POPOVER,
        // Position: bottom of dropdown at the top of the anchor, right-aligned.
        top: pos.top,
        left: pos.left,
        transform: "translate(-100%, -100%)",
      }}
    >
      {/* Header */}
      <div className={`sticky top-0 ${surfaces.elevated} border-b ${borders.default} px-4 py-3 flex items-start justify-between gap-2 z-10`}>
        <div className="min-w-0">
          <h3 className={`${typography.label} ${textColors.primary} font-semibold`}>
            Website Profile
          </h3>
          <p className={`${typography.caption} ${textColors.muted} mt-0.5`}>
            The AI uses this to edit your site in your style and for your goals.
          </p>
        </div>
        <button
          type="button"
          onClick={handleCancel}
          className={`p-1 rounded ${textColors.muted} ${surfaces.hover} flex-shrink-0`}
          aria-label="Close"
          title="Close"
        >
          <CloseIcon className="w-4 h-4" />
        </button>
      </div>

      {/* ── Error view ─────────────────────────────────────────────── */}
      {showErrorView ? (
        <div className="px-4 py-6 flex flex-col items-center gap-4">
          <p className={`${typography.bodySmall} ${textColors.secondary} text-center`}>
            Failed to load your website profile.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchProfile}
              className={`px-3 py-1.5 rounded-md border text-xs font-medium ${borders.default} ${surfaces.primary} ${surfaces.hover} ${textColors.secondary}`}
            >
              Retry
            </button>
            <button
              type="button"
              onClick={handleCreateFromScratch}
              className="px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Create from scratch
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* ── Normal body ──────────────────────────────────────────── */}
          <div className="px-4 py-3 space-y-4">
            {loading ? (
              <div className={`text-center py-6 ${typography.bodySmall} ${textColors.muted}`}>
                Loading…
              </div>
            ) : (
              <>
                {/* Primary goal */}
                <div>
                  <div className={`${typography.caption} ${textColors.secondary} mb-1.5 font-medium`}>
                    Primary goal
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {PRIMARY_GOAL_OPTIONS.map((opt) => {
                      const selected = primaryGoal === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleGoalSelect(opt.value)}
                          className={`px-2 py-1 rounded-md border text-xs font-medium transition-colors whitespace-nowrap ${
                            selected
                              ? "border-green-400 bg-green-400 text-white dark:text-white"
                              : `${borders.default} ${surfaces.primary} ${surfaces.hover} ${textColors.secondary}`
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  {primaryGoal === "other" && (
                    <input
                      type="text"
                      value={primaryGoalOtherText}
                      onChange={(e) => setPrimaryGoalOtherText(e.target.value)}
                      placeholder="Describe your goal…"
                      className={`mt-1.5 w-full px-2.5 py-1.5 border rounded-md text-xs ${surfaces.primary} ${textColors.primary} focus:outline-none focus:ring-2 focus:ring-blue-500 ${borders.default}`}
                    />
                  )}
                </div>

                {/* Tone */}
                <div>
                  <div className={`${typography.caption} ${textColors.secondary} mb-1.5 font-medium`}>
                    Tone
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {TONE_OPTIONS.map((opt) => {
                      const selected = tone === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleToneSelect(opt.value)}
                          className={`px-2 py-1 rounded-md border text-xs font-medium transition-colors whitespace-nowrap ${
                            selected
                              ? "border-green-400 bg-green-400 text-white dark:text-white"
                              : `${borders.default} ${surfaces.primary} ${surfaces.hover} ${textColors.secondary}`
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Content format slider */}
                <div>
                  <div className={`${typography.caption} ${textColors.secondary} mb-1.5 font-medium`}>
                    Content format
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={5}
                    step={1}
                    value={contentFormat}
                    onChange={(e) => setContentFormat(Number(e.target.value))}
                    className="w-full"
                    aria-label="Content format preference"
                  />
                  <div className={`mt-1 grid grid-cols-5 gap-1 ${textColors.tertiary}`} style={{ fontSize: "0.6rem" }}>
                    <div className="text-left">Bullets</div>
                    <div className="text-center">More bullets</div>
                    <div className="text-center">Mixed</div>
                    <div className="text-center">More text</div>
                    <div className="text-right">Text</div>
                  </div>
                </div>

                {/* Content length slider */}
                <div>
                  <div className={`${typography.caption} ${textColors.secondary} mb-1.5 font-medium`}>
                    Content length
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={5}
                    step={1}
                    value={contentLength}
                    onChange={(e) => setContentLength(Number(e.target.value))}
                    className="w-full"
                    aria-label="Content length preference"
                  />
                  <div className={`mt-1 grid grid-cols-5 gap-1 ${textColors.tertiary}`} style={{ fontSize: "0.6rem" }}>
                    <div className="text-left">Succinct</div>
                    <div className="text-center">Short</div>
                    <div className="text-center">Neutral</div>
                    <div className="text-center">Detailed</div>
                    <div className="text-right">Very detailed</div>
                  </div>
                </div>

                {/* Additional instructions */}
                <div>
                  <div className={`${typography.caption} ${textColors.secondary} mb-1.5 font-medium`}>
                    Additional instructions
                  </div>
                  <textarea
                    value={additionalInstructions}
                    onChange={(e) => setAdditionalInstructions(e.target.value)}
                    placeholder="Any extra context for the AI…"
                    rows={3}
                    className={`w-full px-2.5 py-1.5 border rounded-md text-xs resize-none ${surfaces.primary} ${textColors.primary} focus:outline-none focus:ring-2 focus:ring-blue-500 ${borders.default}`}
                  />
                </div>
              </>
            )}
          </div>

          {/* Footer — Save / Cancel */}
          {!loading && (
            <div className={`sticky bottom-0 ${surfaces.elevated} border-t ${borders.default} px-4 py-2.5 flex items-center justify-end gap-2`}>
              <button
                type="button"
                onClick={handleCancel}
                className={`px-3 py-1.5 rounded-md border text-xs font-medium ${borders.default} ${surfaces.primary} ${surfaces.hover} ${textColors.secondary}`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className={`px-3 py-1.5 rounded-md text-xs font-medium text-white transition-colors ${
                  saving
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );

  return ReactDOM.createPortal(
    <>
      {/* Backdrop: clicking outside the panel should behave exactly like Cancel. */}
      <button
        type="button"
        aria-label="Close website profile overlay"
        onMouseDown={handleCancel}
        onTouchStart={handleCancel}
        className="fixed inset-0 bg-transparent p-0 m-0 border-0"
        style={{ zIndex: Z_INDEX.POPOVER - 1 }}
      />
      {dropdown}
    </>,
    document.body
  );
};

export default WebsiteProfileDropdown;
